import { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Shield, RotateCcw } from 'lucide-react';
import { adminApi } from '../utils/api';
import Layout from '../components/Layout';
import Header from '../components/Header';
import { Button, Card, Input, Select, Badge, Spinner, ErrorState } from '../components/ui';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { planToBadgeVariant } from '../utils/statusBadge';
import type { User, PlanType } from '../types';

interface AdminUsersPageProps {
  user: User;
}

interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  plan_type: PlanType;
  sessions_used: number;
  hours_used: number;
  sessions_limit: number;
  hours_limit: number;
  extra_hours?: number;  // 無期限の追加時間。応答に無ければ0として扱う
  extra_sessions?: number;  // 無期限の追加回数。応答に無ければ0として扱う
  created_at: string;
  last_transcription_at: string | null;
}

const PLAN_LABELS: Record<PlanType, string> = {
  free: '無料',
  lite: 'ライト',
  standard: 'スタンダード',
  unlimited: '無制限',
};

const PLAN_OPTIONS: PlanType[] = ['free', 'lite', 'standard', 'unlimited'];

export default function AdminUsersPage({ user: currentUser }: AdminUsersPageProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState<string>('');
  const [updating, setUpdating] = useState<string | null>(null);

  // 意図的: page / filterPlan の変化で即時再取得する。
  // search（テキスト）は依存に含めず、検索ボタン送信（handleSearch）でのみ反映する（元実装の挙動踏襲）。
  useEffect(() => {
    loadUsers();
  }, [page, filterPlan]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getUsers(page, 50, search || undefined, filterPlan || undefined);
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('ユーザー一覧の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadUsers();
  };

  const handlePlanChange = async (userId: string, newPlan: PlanType) => {
    try {
      setUpdating(userId);
      await adminApi.updateUserPlan(userId, newPlan);
      setUsers(users.map(u => u.id === userId ? { ...u, plan_type: newPlan } : u));
    } catch (err) {
      console.error('Failed to update plan:', err);
      alert('プランの更新に失敗しました');
    } finally {
      setUpdating(null);
    }
  };

  const handleToggleAdmin = async (userId: string, currentIsAdmin: boolean) => {
    if (userId === currentUser.id) {
      alert('自分自身の管理者権限は変更できません');
      return;
    }

    const action = currentIsAdmin ? '解除' : '付与';
    if (!confirm(`このユーザーの管理者権限を${action}しますか？`)) {
      return;
    }

    try {
      setUpdating(userId);
      await adminApi.toggleAdminStatus(userId, !currentIsAdmin);
      alert(`管理者権限を${action}しました`);
    } catch (err) {
      console.error('Failed to toggle admin:', err);
      alert('管理者権限の更新に失敗しました');
    } finally {
      setUpdating(null);
    }
  };

  const handleResetUsage = async (userId: string) => {
    if (!confirm('このユーザーの使用量をリセットしますか？（セッション数・時間がゼロになります）')) {
      return;
    }

    try {
      setUpdating(userId);
      await adminApi.resetUserUsage(userId);
      // リストを再読み込み
      await loadUsers();
      alert('使用量をリセットしました');
    } catch (err) {
      console.error('Failed to reset usage:', err);
      alert('使用量のリセットに失敗しました');
    } finally {
      setUpdating(null);
    }
  };

  const handleResetAllExpired = async () => {
    if (!confirm('請求サイクルが終了した全ユーザーの使用量をリセットしますか？')) {
      return;
    }

    try {
      setUpdating('all');
      const result = await adminApi.resetAllExpiredUsage();
      await loadUsers();
      alert(`${result.reset_count}人のユーザーの使用量をリセットしました`);
    } catch (err) {
      console.error('Failed to reset all usage:', err);
      alert('使用量のリセットに失敗しました');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('ja-JP');
  };

  const formatUsage = (used: number, limit: number, isHours: boolean) => {
    if (limit === -1) {
      return isHours ? `${used.toFixed(1)}h / 無制限` : `${used} / 無制限`;
    }
    return isHours ? `${used.toFixed(1)}h / ${limit}h` : `${used} / ${limit}`;
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'user',
      header: 'ユーザー',
      render: (u) => (
        <div>
          <div className="font-medium">{u.display_name || '(未設定)'}</div>
          <div className="text-sm text-text-sub">{u.email}</div>
          <div className="text-xs text-text-sub font-mono">{u.id.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      key: 'plan',
      header: 'プラン',
      render: (u) => <Badge variant={planToBadgeVariant(u.plan_type)}>{PLAN_LABELS[u.plan_type]}</Badge>,
    },
    {
      key: 'usage',
      header: '使用状況',
      render: (u) => (
        <div className="text-sm">
          <div>セッション: {formatUsage(u.sessions_used, u.sessions_limit, false)}</div>
          <div>時間: {formatUsage(u.hours_used, u.hours_limit, true)}</div>
        </div>
      ),
    },
    {
      key: 'extra',
      header: '追加',
      // 0 も省略せず表示する（補填・購入の反映確認のため）。時間の表記は使用状況列の toFixed(1) にそろえる
      render: (u) => (
        <div className="text-sm">
          <div>時間: {(u.extra_hours ?? 0).toFixed(1)}h</div>
          <div>回数: {u.extra_sessions ?? 0}回</div>
        </div>
      ),
    },
    { key: 'created', header: '登録日', render: (u) => <span className="text-sm">{formatDate(u.created_at)}</span> },
    { key: 'last', header: '最終利用', render: (u) => <span className="text-sm">{formatDate(u.last_transcription_at)}</span> },
  ];

  const rowActions = (u: AdminUser) => (
    // 整列は actionsAlign="center"（ResponsiveTable）と一致させる。元実装の操作列も中央寄せ。
    <div className="flex items-center justify-center gap-2">
      <div className="w-32">
        <Select
          value={u.plan_type}
          onChange={(e) => handlePlanChange(u.id, e.target.value as PlanType)}
          disabled={updating === u.id}
          aria-label={`プラン変更: ${u.display_name || u.email}`}
          className="text-sm"
        >
          {PLAN_OPTIONS.map((plan) => (
            <option key={plan} value={plan}>{PLAN_LABELS[plan]}</option>
          ))}
        </Select>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleResetUsage(u.id)}
        disabled={updating === u.id}
        aria-label={`使用量をリセット: ${u.display_name || u.email}`}
        title="使用量をリセット"
      >
        <RotateCcw className="w-5 h-5 text-info" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        // 第2引数 false は「付与専用」導線（元実装踏襲・解除UIは未提供）。
        // 解除UIを追加する際は実 is_admin 状態を渡すこと。
        onClick={() => handleToggleAdmin(u.id, false)}
        disabled={updating === u.id || u.id === currentUser.id}
        aria-label={`管理者権限を付与: ${u.display_name || u.email}`}
        title="管理者権限を付与"
      >
        <Shield className="w-5 h-5 text-text-sub" />
      </Button>
    </div>
  );

  return (
    <Layout>
      <Header user={currentUser} title="otomochi - ユーザー管理" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* 検索・フィルター */}
        <Card className="mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="メールアドレスまたは表示名で検索..."
                  aria-label="メールアドレスまたは表示名で検索"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Select
                value={filterPlan}
                aria-label="プランで絞り込み"
                onChange={(e) => {
                  setFilterPlan(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">全プラン</option>
                {PLAN_OPTIONS.map((plan) => (
                  <option key={plan} value={plan}>{PLAN_LABELS[plan]}</option>
                ))}
              </Select>
            </div>
            <Button type="submit">検索</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleResetAllExpired}
              disabled={updating === 'all'}
              className="gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {updating === 'all' ? '処理中...' : '期限切れを一括リセット'}
            </Button>
          </form>
        </Card>

        {/* ユーザー一覧 */}
        {loading ? (
          <div className="text-center py-12">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-text-sub">読み込み中...</p>
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={loadUsers} />
        ) : (
          <>
            <Card padding="none" className="overflow-hidden p-4">
              <ResponsiveTable
                columns={columns}
                rows={users}
                rowKey={(u) => u.id}
                rowActions={rowActions}
                actionsHeader="操作"
                actionsAlign="center"
                emptyMessage="ユーザーが見つかりません"
                caption="ユーザー一覧"
              />
            </Card>

            {/* ページネーション */}
            <div className="flex justify-center items-center gap-4 mt-6">
              <Button
                variant="secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                前へ
              </Button>
              <span className="text-text-sub">ページ {page}</span>
              <Button
                variant="secondary"
                onClick={() => setPage(p => p + 1)}
                disabled={users.length < 50}
                className="gap-1"
              >
                次へ
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </main>
    </Layout>
  );
}
